// src/ConfigContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const defaultConfig = {
  season: null,
  taps: null,
  booster: null,
  shop: null,
  evolvements: null,
  referrers: null,
  daily: null,
  whitelistEnabled: undefined,
  newUsers: null,
  discounts: null,
  arenas: null,
  tournaments: { showBadge: false, tournaments: [] },
  tournamentsActive: [],
  fortuneWheel: {
  spinPrice: 0,
  cooldownSeconds: 86400,
  slots: Array.from({ length: 8 }).map(() => ({
    prizeItemId: null,
    prizeGamepassItemId: null,
    weight: 1,
    item: null,
  })),
  },
  hiddenSeek: {
    enabled: false,
    entryFeeRunes: 0,
    cooldownSeconds: 86400,
    roundDurationSeconds: 60,
    maxRoundsPerDay: 1,
    rewardItems: false,
    sparksPrize: 0,
    runesPrize: 0,
    scenes: [],
  },
};

const ConfigContext = createContext({ ...defaultConfig, loaded: false, refresh: () => {} });

/* ---------------- Normalizers (keep UI from crashing if CMS fields are missing) ---------------- */
const toNum = (v, d = 0) => (Number.isFinite(+v) ? +v : d);

function normalizeEvolvements(raw) {
  // ✅ NEW SHAPE: { prices: { common: [{level,runes,cardPieces}], ... } }
  if (raw?.prices && typeof raw.prices === "object") {
    const prices = raw.prices;

    const normalizeTier = (list) =>
      (Array.isArray(list) ? list : []).map((row, idx) => ({
        level: Number.isFinite(Number(row?.level)) ? Number(row.level) : idx + 1,
        runes: Number.isFinite(Number(row?.runes)) ? Number(row.runes) : 0,
        cardPieces: Number.isFinite(Number(row?.cardPieces)) ? Number(row.cardPieces) : 0,
        sparks: Number.isFinite(Number(row?.sparks)) ? Number(row.sparks) : 0,
      }));

    return {
      prices: {
        common: normalizeTier(prices.common),
        rare: normalizeTier(prices.rare),
        epic: normalizeTier(prices.epic),
        legendary: normalizeTier(prices.legendary),
      },
    };
  }

  const listToMap = (list) => {
    const out = {};
    const arr = Array.isArray(list) ? list : [];
    for (const row of arr) {
      const key = String(row?.rarity ?? row?.RARITY ?? "").trim().toLowerCase();
      const val = Number(row?.cost ?? row?.COST ?? row?.price ?? row?.PRICE);
      if (["common", "rare", "epic", "legendary"].includes(key) && Number.isFinite(val) && val >= 0) {
        out[key] = val;
      }
    }
    return out;
  };

  const byRunes = Array.isArray(raw?.byRunes) ? raw.byRunes : [];
  const byPieces = Array.isArray(raw?.byPieces) ? raw.byPieces : [];

  return { byRunes, byPieces, runes: listToMap(byRunes), pieces: listToMap(byPieces) };
}

function normalizeIsoLike(value) {
  if (!value) return null;

  const raw = String(value).trim();

  const fixed = raw.replace(
    /T(\d):(\d{2}:\d{2}(?:\.\d{1,3})?Z)$/i,
    "T0$1:$2"
  );

  const ms = Date.parse(fixed);
  if (!Number.isFinite(ms)) return value;

  return new Date(ms).toISOString();
}

function normalizeShop(raw) {

  const rewardPercent = toNum(
    raw?.rewardPercent ?? raw?.REWARD_PERCENT ?? raw?.fight?.rewardPercent, 0
  );

  const packs = {
    common: toNum(raw?.packs?.common),
    rare: toNum(raw?.packs?.rare),
    epic: toNum(raw?.packs?.epic),
    legendary: toNum(raw?.packs?.legendary),
  };
  const runes  = Array.isArray(raw?.runes)  ? raw.runes .map(s => ({ amount: toNum(s.amount), price: toNum(s.price) })).filter(s => s.amount && s.price) : [];
    const sparks = Array.isArray(raw?.sparks)
  ? raw.sparks
      .map((s) => ({
        ...s,

        amount: toNum(
          s?.amount ??
          s?.AMOUNT,
          0
        ),

        price: toNum(
          s?.price ??
          s?.PRICE,
          0
        ),

        discountStars: toNum(
          s?.discountStars ??
          s?.discount_stars ??
          s?.DISCOUNT_STARS,
          0
        ),

        priceTon: toNum(
          s?.priceTon ??
          s?.price_ton ??
          s?.tonPrice ??
          s?.ton_price ??
          s?.PRICE_TON ??
          0,
          0
        ),

        skuId: String(
          s?.skuId ??
          s?.sku_id ??
          s?.SKU_ID ??
          s?.id ??
          ""
        ).trim(),
      }))
      .filter((s) => s.amount > 0 && s.price > 0)
  : [];

  const evolve = {
    common: toNum(raw?.evolve?.common, 5),
    rare: toNum(raw?.evolve?.rare, 10),
    epic: toNum(raw?.evolve?.epic, 15),
    legendary: toNum(raw?.evolve?.legendary, 20),
  };
  const fight = {
    common: toNum(raw?.fight?.common),
    rare: toNum(raw?.fight?.rare),
    epic: toNum(raw?.fight?.epic),
    legendary: toNum(raw?.fight?.legendary),
  };

   const fightCost = {
    common:    toNum(raw?.fightCost?.common ?? raw?.fight?.common),
    rare:      toNum(raw?.fightCost?.rare   ?? raw?.fight?.rare),
    epic:      toNum(raw?.fightCost?.epic   ?? raw?.fight?.epic),
    legendary: toNum(raw?.fightCost?.legendary ?? raw?.fight?.legendary),
  };

  const cardMerging = {
  common: {
    sparks: toNum(raw?.cardMerging?.common?.sparks ?? raw?.CARD_MERGING?.common?.sparks, 0),
    runes: toNum(raw?.cardMerging?.common?.runes ?? raw?.CARD_MERGING?.common?.runes, 0),
  },
  rare: {
    sparks: toNum(raw?.cardMerging?.rare?.sparks ?? raw?.CARD_MERGING?.rare?.sparks, 0),
    runes: toNum(raw?.cardMerging?.rare?.runes ?? raw?.CARD_MERGING?.rare?.runes, 0),
  },
  epic: {
    sparks: toNum(raw?.cardMerging?.epic?.sparks ?? raw?.CARD_MERGING?.epic?.sparks, 0),
    runes: toNum(raw?.cardMerging?.epic?.runes ?? raw?.CARD_MERGING?.epic?.runes, 0),
  },
  legendary: {
    sparks: toNum(raw?.cardMerging?.legendary?.sparks ?? raw?.CARD_MERGING?.legendary?.sparks, 0),
    runes: toNum(raw?.cardMerging?.legendary?.runes ?? raw?.CARD_MERGING?.legendary?.runes, 0),
  },
};

const cardSidePriceSparks = toNum(
  raw?.cardSidePriceSparks ??
  raw?.CARD_SIDE_PRICE_SPARKS,
  0
);

const cardSidePriceRunes = toNum(
  raw?.cardSidePriceRunes ??
  raw?.CARD_SIDE_PRICE_RUNES,
  0
);

const cardSidePriceStars = toNum(
  raw?.cardSidePriceStars ??
  raw?.CARD_SIDE_PRICE_STARS,
  0
);

   const changeSidePrice = toNum(
    raw?.changeSidePrice ?? raw?.CHANGE_SIDE_PRICE,
    0
  );

   const nameChangePrice = toNum(
  raw?.nameCardPrice ?? 
  raw?.NAME_CARD_PRICE ??
  raw?.nameChangePrice ??
  raw?.NAME_CHANGE_PRICE ??
  raw?.cardNamePrice ??
  raw?.CARD_NAME_PRICE ??
  0
);

   const gamePassPrice = toNum(
    raw?.gamePassPrice ?? raw?.GAME_PASS_PRICE,
    0
  );

   const gamePassPriceStars = toNum(
  raw?.gamePassPriceStars ?? raw?.GAME_PASS_PRICE_STARS,
  0
);

   const abilityChangePriceRunes = toNum(
  raw?.abilityChangePriceRunes ?? raw?.ABILITY_CHANGE_PRICE_RUNES,
  0
);

   const refillPriceRunes = toNum(
  raw?.refillPriceRunes ?? raw?.REFILL_PRICE_RUNES,
  0
);

const abilityChangePriceSparks = toNum(
  raw?.abilityChangePriceSparks ?? raw?.ABILITY_CHANGE_PRICE_SPARKS,
  0
);

  return {
  packs,
  runes,
  sparks,
  evolve,
  fight,
  fightCost,
  cardMerging,
  rewardPercent,

  changeSidePrice,

  cardSidePriceSparks,
  cardSidePriceRunes,
  cardSidePriceStars,

  nameChangePrice,
  gamePassPrice,
  gamePassPriceStars,
  abilityChangePriceRunes,
  abilityChangePriceSparks,
  refillPriceRunes
};
}

function normalizeTaps(raw) {
  return {
    total: toNum(raw?.total),
    countdownTime: toNum(raw?.countdownTime),
    countdownEnabled: !!raw?.countdownEnabled,
    runesReward: toNum(raw?.runesReward),
  };
}

function normalizeBooster(raw) {
  const tiers = Array.isArray(raw?.tiers)
    ? raw.tiers.map(t => ({ increment: toNum(t.increment), price: toNum(t.price) }))
    : [];
  return { tiers };
}

function normalizeReferrers(raw) {
  return {
    runePurchaseCommissionPercent:
      toNum(
        raw?.runePurchaseCommissionPercent,
        0
      ),

    sparkPurchaseCommissionPercent:
      toNum(
        raw?.sparkPurchaseCommissionPercent,
        0
      ),

    qualifiedReferralSparks:
      toNum(
        raw?.qualifiedReferralSparks,
        0
      ),

    qualifiedReferralRunes:
      toNum(
        raw?.qualifiedReferralRunes,
        0
      ),

    qualification: {
      battlesRequired:
        toNum(
          raw?.qualification
            ?.battlesRequired,
          0
        ),

      requireRunePurchase:
        Boolean(
          raw?.qualification
            ?.requireRunePurchase
        ),

      requireSparkPurchase:
        Boolean(
          raw?.qualification
            ?.requireSparkPurchase
        ),
    },
  };
}

/* -------------------------------------- Provider -------------------------------------- */
export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(defaultConfig);
  const [loaded, setLoaded] = useState(false);
  const [whitelistKeyMissing, setWhitelistKeyMissing] = useState(false);
  const lastConfigRef = useRef(defaultConfig);
  const etagRef = useRef(null);

  const normalizeDiscounts = (raw) => {
  const enabled = Boolean(raw?.ENABLED ?? raw?.enabled ?? false);
  const offersIn = raw?.OFFERS ?? raw?.offers ?? [];
  const offersArr = Array.isArray(offersIn) ? offersIn : [];

  const offers = offersArr.map((o, idx) => {
    const type = String(o?.TYPE ?? o?.type ?? 'RUNES').trim().toUpperCase();
    const priceType = String(o?.PRICE_TYPE ?? o?.priceType ?? 'STARS').trim().toUpperCase();

    const tr = o?.TRIGGERS ?? o?.triggers ?? {};

    const numOr = (v, d = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : d;
    };

    return {
  id: String(o?.ID ?? o?.id ?? `offer-${idx + 1}`),
  enabled: Boolean(o?.ENABLED ?? o?.enabled ?? true),
  type: ['RUNES', 'SPARKS', 'INFO'].includes(type) ? type : 'RUNES',
  grantAmount: Number(o?.GRANT_AMOUNT ?? o?.grantAmount ?? 0) || 0,
  priceType: ['RUNES', 'SPARKS', 'STARS'].includes(priceType) ? priceType : 'STARS',
  priceAmount: Number(o?.PRICE_AMOUNT ?? o?.priceAmount ?? 0) || 0,
  validForSeconds: Number(o?.VALID_FOR_SECONDS ?? o?.validForSeconds ?? 0) || 0,

  title: String(o?.TITLE ?? o?.title ?? "").trim(),
  subtitle: String(o?.SUBTITLE ?? o?.subtitle ?? "").trim(),
  content: String(o?.CONTENT ?? o?.content ?? "").trim(),
  footerText: String(o?.FOOTER_TEXT ?? o?.footerText ?? o?.footer ?? "").trim(),
  avatar: String(o?.AVATAR ?? o?.avatar ?? "").trim(),

  triggers: {
    runesLt: Number(tr?.RUNES_LT ?? tr?.runesLt ?? 0) || 0,
    sparksLt: Number(tr?.SPARKS_LT ?? tr?.sparksLt ?? 0) || 0,
    onlyReferred: Boolean(tr?.ONLY_REFERRED ?? tr?.onlyReferred ?? false),
    cooldownHours: Number(tr?.COOLDOWN_HOURS ?? tr?.cooldownHours ?? 24) || 24,
    maxShowsPerDay: Number(tr?.MAX_SHOWS_PER_DAY ?? tr?.maxShowsPerDay ?? 1) || 1,
  },
};

  });

  return { enabled, offers };
};



  const makeConfig = (data) => {
    const safeDaily = data?.daily && Array.isArray(data.daily.rewards) ? data.daily : null;

        // STRICT: read from CMS only — try a few plausible shapes, but do not invent a value
    const wlRaw =
      (typeof data?.whitelistEnabled === 'boolean') ? data.whitelistEnabled :
      (typeof data?.flags?.whitelistEnabled === 'boolean') ? data.flags.whitelistEnabled :
      (typeof data?.whitelist?.enabled === 'boolean') ? data.whitelist.enabled :
      undefined;

    if (typeof wlRaw !== 'boolean') {
      console.error(
        '[CONFIG] Missing "whitelistEnabled" boolean in /api/config. ' +
        'Expected one of: root.whitelistEnabled, flags.whitelistEnabled, whitelist.enabled'
      );


      setWhitelistKeyMissing(true);
    } else {
      setWhitelistKeyMissing(false);
    }

    const nowMs = Date.now();
    
    const tRaw = data?.tournaments ?? data?.['tournaments'] ?? null;

    const showBadge = Boolean(tRaw?.showBadge);

    const allTournaments = (Array.isArray(tRaw?.tournaments) ? tRaw.tournaments : []).map((t) => ({
  ...t,
  startAt: normalizeIsoLike(t?.startAt ?? t?.start_date),
  endAt: normalizeIsoLike(t?.endAt ?? t?.end_date),
  priceSparks: Number.isFinite(Number(t?.priceSparks)) ? Number(t.priceSparks) : 0,
  priceRunes: Number.isFinite(Number(t?.priceRunes)) ? Number(t.priceRunes) : 0,
}));

    const tournaments = {
  showBadge,
  tournaments: allTournaments,
};

    const activeComputed = allTournaments.filter((t) => {
      const s = Date.parse(t?.startAt);
      const e = Date.parse(t?.endAt);
      return Number.isFinite(s) && Number.isFinite(e) && s <= nowMs && nowMs < e;
    });

    let tournamentsActive = Array.isArray(data?.tournamentsActive)
      ? data.tournamentsActive
      : activeComputed;

    if (Boolean(tournaments?.showBadge) && (!Array.isArray(tournamentsActive) || tournamentsActive.length === 0)) {
      const lastEnded = allTournaments
        .map((t) => ({ t, endMs: Date.parse(t?.endAt) }))
        .filter(({ endMs }) => Number.isFinite(endMs) && endMs <= nowMs)
        .sort((a, b) => b.endMs - a.endMs)[0]?.t ?? null;

      tournamentsActive = lastEnded ? [lastEnded] : [];
    }

    const nuRaw =
      data?.newUsers ??
      data?.['new-users'] ??
      null;

    const toNum = (v, d = 0) => (Number.isFinite(+v) ? +v : d);
    const allowedR = new Set(['None','Common','Rare','Epic','Legendary']);
    const newUsers = nuRaw && typeof nuRaw === 'object'
      ? {
          bonusSparks:  toNum(nuRaw.bonusSparks ?? nuRaw.BONUS_SPARKS, 0),
          bonusRunes:   toNum(nuRaw.bonusRunes  ?? nuRaw.BONUS_RUNES,  0),
          bonusCredits: toNum(nuRaw.bonusCredits?? nuRaw.BONUS_CREDITS,0),
           bonusSparksFromReferrer: toNum(
        nuRaw.bonusSparksFromReferrer ?? nuRaw.BONUS_SPARKS_FROM_REFERRER,
        0
      ),
      bonusRunesFromReferrer: toNum(
        nuRaw.bonusRunesFromReferrer ?? nuRaw.BONUS_RUNES_FROM_REFERRER,
        0
      ),
          starterRarity: allowedR.has(String(nuRaw.starterRarity ?? nuRaw.STARTER_RARITY ?? 'None'))
            ? String(nuRaw.starterRarity ?? nuRaw.STARTER_RARITY)
            : 'None',
        }
      : null;

    return {
      season: data?.season ?? null,                              // expect { season_number, start_date, end_date, is_active }
      taps: data?.taps ? normalizeTaps(data.taps) : null,
      booster: data?.booster ? normalizeBooster(data.booster) : null,
      shop: data?.shop ? normalizeShop(data.shop) : null,
      evolvements: data?.evolvements ? normalizeEvolvements(data.evolvements) : null,
      referrers: data?.referrers ? normalizeReferrers(data.referrers) : null,
      daily: safeDaily,
      whitelistEnabled: wlRaw,
      newUsers,

      fortuneWheel: (() => {
  const fw = data?.fortuneWheel || data?.["fortune-wheel"] || null;

  const spinPriceRunes = Number(
    fw?.spinPriceRunes ??
    fw?.spin_price_runes ??
    fw?.spinPriceRunesAmount ?? // optional aliases if you ever had them
    0
  );

  const cooldownSeconds = Number(fw?.cooldownSeconds ?? fw?.cooldown_seconds ?? 86400);

  const slotsIn = Array.isArray(fw?.slots) ? fw.slots : [];
  const slots = Array.from({ length: 8 }).map((_, i) => {
    const s = slotsIn[i] || {};
    return {
      prizeItemId: s.prizeItemId ?? s.prize_item_id ?? null,
      prizeGamepassItemId: s.prizeGamepassItemId ?? s.prize_gamepass_item_id ?? null,
      weight: Number.isFinite(Number(s.weight)) ? Number(s.weight) : 1,
      item: s.item ?? null,
    };
  });

  return {
    spinPriceRunes: Number.isFinite(spinPriceRunes) && spinPriceRunes >= 0 ? spinPriceRunes : 0,
    cooldownSeconds: Number.isFinite(cooldownSeconds) && cooldownSeconds >= 0 ? cooldownSeconds : 86400,
    slots,
  };
})(),

      hiddenSeek: (() => {
        const hs = data?.hiddenSeek || data?.["hidden-seek"] || null;

        if (!hs) {
  return {
    enabled: false,
    entryFeeRunes: 0,
    cooldownSeconds: 86400,
    roundDurationSeconds: 60,
    maxRoundsPerDay: 1,

    // NEW
    rewardItems: false,
    sparksPrize: 0,
    runesPrize: 0,

    scenes: [],
  };
}

        return {
          enabled: Boolean(hs?.enabled),
          entryFeeRunes: Number(hs?.entryFeeRunes ?? 0) || 0,
          cooldownSeconds: Number(hs?.cooldownSeconds ?? 86400) || 86400,
          roundDurationSeconds: Number(hs?.roundDurationSeconds ?? 60) || 60,
          maxRoundsPerDay: Number(hs?.maxRoundsPerDay ?? 1) || 1,
          rewardItems: Boolean(hs?.rewardItems),
          sparksPrize: Number(hs?.sparksPrize ?? 0) || 0,
          runesPrize: Number(hs?.runesPrize ?? 0) || 0,
          scenes: Array.isArray(hs?.scenes)
            ? hs.scenes.map((scene) => ({
                ...scene,
                active: scene?.active !== false,
                objects: Array.isArray(scene?.objects)
                  ? scene.objects.map((obj) => ({
                      ...obj,
                      x: Number(obj?.x ?? 0) || 0,
                      y: Number(obj?.y ?? 0) || 0,
                      radius: Number(obj?.radius ?? 5) || 5,
                      quantity: Number(obj?.quantity ?? 1) || 1,
                    }))
                  : [],
              }))
            : [],
        };
      })(),

      tournaments,
      tournamentsActive,
      tournamentBadgeEnabled: tournaments.showBadge,
      hasActiveTournament: tournamentsActive.length > 0,

      discounts: data?.discounts ? normalizeDiscounts(data.discounts)
         : data?.DISCOUNTS ? normalizeDiscounts(data.DISCOUNTS)
         : null,
        arenas: (() => {
  const rawArenas = (data?.arenas && Array.isArray(data.arenas.arenas))
    ? data.arenas
    : { arenas: [] };

  const speedups = rawArenas?.chestSpeedupByType || {};

  return {
    ...rawArenas,
    arenas: (rawArenas.arenas || []).filter((arena) => arena?.enabled !== false),
    chestSpeedupByType: {
      wood: {
        runes: Number(speedups?.wood?.runes ?? 0) || 0,
        sparks: Number(speedups?.wood?.sparks ?? 0) || 0,
        time: Number(speedups?.wood?.time ?? 0) || 0,
      },
      silver: {
        runes: Number(speedups?.silver?.runes ?? 0) || 0,
        sparks: Number(speedups?.silver?.sparks ?? 0) || 0,
        time: Number(speedups?.silver?.time ?? 0) || 0,
      },
      golden: {
        runes: Number(speedups?.golden?.runes ?? 0) || 0,
        sparks: Number(speedups?.golden?.sparks ?? 0) || 0,
        time: Number(speedups?.golden?.time ?? 0) || 0,
      },
      diamond: {
        runes: Number(speedups?.diamond?.runes ?? 0) || 0,
        sparks: Number(speedups?.diamond?.sparks ?? 0) || 0,
        time: Number(speedups?.diamond?.time ?? 0) || 0,
      },
    },
  };
})(),
    };
  };

  const refresh = async () => {
    const ctrl = new AbortController();
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/config`, {
        method: 'GET',
        headers: etagRef.current ? { 'If-None-Match': etagRef.current } : undefined,
        signal: ctrl.signal,
      });

      if (res.status === 304) {
        setConfig(lastConfigRef.current);
        setLoaded(true);
        return;
      }

      const raw = await res.text();

      let data = {};
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.error("[CONFIG] JSON INVALIDO no /api/config:", e);
        throw new Error("CONFIG JSON INVALIDO");
      }

      const next = makeConfig(data);
      lastConfigRef.current = next;

      const etag = res.headers.get('ETag');
      if (etag) etagRef.current = etag;

      setConfig(next);
      setLoaded(true);
    } catch (e) {
      console.error('Config load failed:', e);
      setConfig(lastConfigRef.current);
      setLoaded(true);
    }
    return () => ctrl.abort();
  };

  useEffect(() => { refresh(); /* on mount */ }, []); // eslint-disable-line

  const value = useMemo(() => ({ ...config, loaded, refresh }), [config, loaded]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

export const useAppConfig = () => useContext(ConfigContext);
